/**
 * Veli portalı.
 *
 * Tasarım kararı: burası klinik bir ekran değil. Veli "d' = 2,8" ya da
 * "çeldirici bedeli 32 ms" görmez; ne yapması gerektiğini ve çocuğunun
 * ne zaman geleceğini görür. Uygulayıcının gönderdiği rapor dışında
 * hiçbir sayı yok.
 */

import { useCallback, useEffect, useState } from 'react'
import { api, type Cocuk, type Veli } from './api'
import { Giris } from './pages/Giris'
import { CocukSayfasi } from './pages/CocukSayfasi'
import { Rapor } from './pages/Rapor'

type Ekran = { ad: 'cocuk' } | { ad: 'rapor'; raporId: string }

export function App() {
  const [veli, setVeli] = useState<Veli | null>(null)
  const [cocuklar, setCocuklar] = useState<Cocuk[] | null>(null)
  const [secili, setSecili] = useState<string | null>(null)
  const [ekran, setEkran] = useState<Ekran>({ ad: 'cocuk' })
  const [yukleniyor, setYukleniyor] = useState(true)
  const [baglantiHatasi, setBaglantiHatasi] = useState(false)
  /**
   * Çocuk listesi alınamadı.
   *
   * `cocuklariYukle`nin kendi yakalayıcısı yoktu: giriş başarılı olup
   * liste isteği düşerse hata, giriş ekranının yakalayıcısına düşüyor
   * ve veliye "Kod doğru değil ya da süresi doldu" yazıyordu —
   * oysa kod doğruydu. Girişi geçtiyse ekran da sonsuza kadar
   * "Yükleniyor…" kalıyordu.
   */
  const [listeHatasi, setListeHatasi] = useState(false)

  const cocuklariYukle = useCallback(async () => {
    try {
      const r = await api.cocuklar()
      setCocuklar(r.cocuklar)
      setSecili((s) => s ?? r.cocuklar[0]?.id ?? null)
      setListeHatasi(false)
    } catch {
      // Hata BURADA kalıyor: girişin yakalayıcısına düşüp yanlış
      // mesaj göstermesin.
      setListeHatasi(true)
    }
  }, [])

  useEffect(() => {
    /**
     * Adres çubuğunda bağlantı jetonu varsa onunla giriliyor.
     *
     * Jeton adresten HEMEN siliniyor: tarayıcı geçmişinde, paylaşılan
     * bir ekran görüntüsünde ya da "bağlantıyı kopyala" ile başkasına
     * giden adreste durmaması gerekiyor. Zaten tek kullanımlık, ama
     * kullanılmadan önce başkasının eline geçmesinin önü de kapanmalı.
     */
    const adres = new URL(location.href)
    const jeton = adres.searchParams.get('g')
    if (jeton) {
      adres.searchParams.delete('g')
      history.replaceState(null, '', adres.pathname + adres.search + adres.hash)
    }

    const baslat = async () => {
      if (jeton) {
        try {
          const r = await api.baglantiGirisi(jeton)
          setVeli(r.veli)
          await cocuklariYukle()
          return
        } catch {
          setBaglantiHatasi(true)
        }
      }
      try {
        const r = await api.ben()
        setVeli(r.veli)
        await cocuklariYukle()
      } catch {
        setVeli(null)
      }
    }

    baslat().finally(() => setYukleniyor(false))
  }, [cocuklariYukle])

  const cikis = useCallback(async () => {
    await api.cikis().catch(() => undefined)
    setVeli(null)
    setCocuklar(null)
    setSecili(null)
    setEkran({ ad: 'cocuk' })
  }, [])

  if (yukleniyor) {
    return (
      <div className="icerik">
        <p className="alt">Yükleniyor…</p>
      </div>
    )
  }

  if (!veli) {
    return (
      <Giris
        baglantiHatasi={baglantiHatasi}
        onGiris={async (v) => {
          setVeli(v)
          await cocuklariYukle()
        }}
      />
    )
  }

  const cocuk = cocuklar?.find((c) => c.id === secili) ?? null

  return (
    <div className="kabuk">
      <header className="ust">
        <span className="marka">Nirengi</span>
        {cocuklar && cocuklar.length > 1 && (
          <nav>
            {cocuklar.map((c) => (
              <button
                key={c.id}
                className={c.id === secili ? 'aktif' : ''}
                onClick={() => {
                  setSecili(c.id)
                  setEkran({ ad: 'cocuk' })
                }}
              >
                {c.ad}
              </button>
            ))}
          </nav>
        )}
        <div className="sag">
          <span>{veli.adSoyad}</span>
          <button onClick={() => void cikis()}>Çıkış</button>
        </div>
      </header>

      <main className="icerik">
        {listeHatasi ? (
          <div className="uyari">
            Bilgiler alınamadı. Bağlantınızı kontrol edip tekrar deneyin.
            <div style={{ marginTop: 8 }}>
              <button
                onClick={() => {
                  setListeHatasi(false)
                  void cocuklariYukle()
                }}
              >
                Tekrar dene
              </button>
            </div>
          </div>
        ) : !cocuklar ? (
          <p className="alt">Yükleniyor…</p>
        ) : cocuklar.length === 0 ? (
          <div className="bilgi">
            Portalda görüntülenecek bir kayıt yok. Merkezle görüşüp veli portalı onayınızın
            tanımlı olduğundan emin olabilirsiniz.
          </div>
        ) : ekran.ad === 'rapor' ? (
          <Rapor raporId={ekran.raporId} onGeri={() => setEkran({ ad: 'cocuk' })} />
        ) : cocuk ? (
          <CocukSayfasi
            cocuk={cocuk}
            onRapor={(raporId) => setEkran({ ad: 'rapor', raporId })}
          />
        ) : null}
      </main>
    </div>
  )
}
